/**
 * Shared plumbing for the Vivlico web components. Components are authored as
 * light-DOM custom elements so existing global CSS, Font Awesome icons and
 * runtime theme variables keep working while markup migrates onto them.
 */

const STYLE_ELEMENT_ID = 'vivDesignSystemStyles';

/** Injects the design-system stylesheet exactly once per document. */
export function ensureDesignSystemStyles(css: string): void {
    if (typeof document === 'undefined') return;
    if (document.getElementById(STYLE_ELEMENT_ID)) return;
    const style = document.createElement('style');
    style.id = STYLE_ELEMENT_ID;
    style.textContent = css;
    document.head.appendChild(style);
}

/** Registers a custom element, ignoring duplicate definitions (HMR / re-import). */
export function defineComponent(tag: string, ctor: CustomElementConstructor): void {
    if (typeof customElements === 'undefined') return;
    if (customElements.get(tag)) return;
    customElements.define(tag, ctor);
}

/** Base class with a `render()` lifecycle hook run on connect and attr change. */
export abstract class VivElement extends HTMLElement {
    protected abstract render(): void;

    connectedCallback(): void {
        this.render();
    }

    attributeChangedCallback(): void {
        if (this.isConnected) this.render();
    }

    protected attr(name: string, fallback = ''): string {
        const value = this.getAttribute(name);
        return value === null ? fallback : value;
    }

    protected hasFlag(name: string): boolean {
        return this.hasAttribute(name) && this.getAttribute(name) !== 'false';
    }
}
